import { Link } from "react-router-dom";
import { useState } from "react";
import { Confirmation, Success, Error } from "../../Components/Modal";
import { useMutation } from "react-query";
import { getCategories, handleCategoryStatus, deleteCategory as deleteCategoryApi } from "../../Services/Category";
import { image_url } from "../../Utils/connection_strings";
import useTableControls from "../../Hooks/useTableControls";
import useFetchData from "../../Hooks/useFetchData";
import Pagination from "../../Components/Table/Pagination";
import PageHeader from "../../Components/PageHeader";
import ModalImageViewer from "../../Components/ModalImageViewer";
import "../../styles/PremiumCardPages.css";

function CategoryCardImage({ src, alt, onClick }) {
    const [hasError, setHasError] = useState(!src);

    if (!src || hasError) {
        return (
            <div className="cat-card__placeholder">
                <div className="cat-card__placeholder-icon">
                    <i className="fas fa-box-open" />
                </div>
                <span>No Image</span>
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            onClick={onClick}
            onError={() => setHasError(true)}
        />
    );
}

export default function CategoryManagement() {
    const {
        perPage,
        status,
        search_string,
        from,
        to,
    } = useTableControls();

    const {
        page,
        setPage,
        isLoading,
        data,
        refetch,
    } = useFetchData("category_logs", getCategories, [
        perPage,
        search_string,
        status,
        from,
        to,
    ]);

    const categories = data?.data?.data?.docs || [];

    const [showImageViewer, setShowImageViewer] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);

    const { mutate: mutateStatus } = useMutation(
        (id) => handleCategoryStatus(id),
        {
            onSuccess: (res) => {
                Success("Status Updated", res.data?.message || "Category status updated successfully.");
                refetch();
            },
            onError: (err) => {
                Error(err?.response?.data?.message || "Something went wrong.");
            },
        }
    );

    const { mutate: mutateDelete } = useMutation(
        (id) => deleteCategoryApi(id),
        {
            onSuccess: (res) => {
                Success("Category Deleted", res.data?.message || "The category has been removed successfully.");
                refetch();
            },
            onError: (err) => {
                Error(err?.response?.data?.message || "Something went wrong.");
            },
        }
    );

    const handleDelete = (id, name) => {
        Confirmation(
            `Are you sure you want to delete the category "${name}"? This action cannot be undone.`,
            "Yes, Delete",
            () => mutateDelete(id)
        );
    };

    const toggleStatus = (id, name, currentStatus) => {
        Confirmation(
            `Are you sure you want to ${currentStatus ? "deactivate" : "activate"} the category "${name}"?`,
            "Yes, Update",
            () => mutateStatus(id)
        );
    };

    const openImageViewer = (categoryImage) => {
        if (!categoryImage) return;
        const imageUrl = categoryImage.startsWith("http")
            ? categoryImage
            : `${clean_image_url}/${categoryImage}`;
        setViewerImages([imageUrl]);
        setShowImageViewer(true);
    };

    const clean_image_url = image_url?.endsWith("/") ? image_url.slice(0, -1) : image_url;

    const getImageUrl = (categoryImage) => {
        if (!categoryImage) return null;
        return categoryImage.startsWith("http")
            ? categoryImage
            : `${clean_image_url}/${categoryImage}`;
    };

    return (
        <div className="app-content content">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="category-management">
                        <div className="user-details-wrapper">
                            <PageHeader
                                title="Category Management"
                                subtitle="Configure logistics item classes and weight parameters"
                                action={
                                    <Link to="/category-management/add" className="pcard-add-btn">
                                        <i className="fas fa-plus me-2" />
                                        Add New Category
                                    </Link>
                                }
                            />

                            <div className="pcard-grid pcard-grid--category">
                                {isLoading && categories.length === 0 ? (
                                    <div className="pcard-loading">
                                        <div className="spinner-border text-warning" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : categories.length > 0 ? (
                                    categories.map((category) => {
                                        const imageUrl = getImageUrl(category.categoryImage);
                                        return (
                                            <article className="cat-card" key={category._id}>
                                                <div className={`cat-card__media ${imageUrl ? "cat-card__media--clickable" : ""}`}>
                                                    <CategoryCardImage
                                                        src={imageUrl}
                                                        alt={category.categoryName}
                                                        onClick={() => imageUrl && openImageViewer(category.categoryImage)}
                                                    />
                                                    <button
                                                        type="button"
                                                        className={`cat-card__status ${category.status ? "cat-card__status--active" : "cat-card__status--inactive"}`}
                                                        onClick={() => toggleStatus(category._id, category.categoryName, category.status)}
                                                    >
                                                        <span className="cat-card__status-dot" />
                                                        {category.status ? "Active" : "Inactive"}
                                                    </button>
                                                </div>

                                                <div className="cat-card__body">
                                                    <div className="cat-card__head">
                                                        <h2 className="cat-card__title">{category.categoryName}</h2>
                                                        <div className="dropdown pcard-dropdown">
                                                            <button
                                                                type="button"
                                                                className="pcard-menu-btn"
                                                                data-bs-toggle="dropdown"
                                                                aria-label="Category actions"
                                                            >
                                                                <i className="fas fa-ellipsis-v" />
                                                            </button>
                                                            <ul className="dropdown-menu dropdown-menu-end">
                                                                <li>
                                                                    <Link
                                                                        className="dropdown-item"
                                                                        to={`/category-management/edit/${category._id}`}
                                                                    >
                                                                        <i className="fas fa-edit me-2 text-primary" />
                                                                        Edit
                                                                    </Link>
                                                                </li>
                                                                <li>
                                                                    <button
                                                                        type="button"
                                                                        className="dropdown-item text-danger"
                                                                        onClick={() => handleDelete(category._id, category.categoryName)}
                                                                    >
                                                                        <i className="fas fa-trash-alt me-2" />
                                                                        Delete
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>

                                                    <p className="cat-card__desc">
                                                        {category.description || "No description provided."}
                                                    </p>

                                                    <div className="cat-card__footer">
                                                        <div className="cat-card__stat">
                                                            <div className="cat-card__stat-icon">
                                                                <i className="fas fa-weight-hanging" />
                                                            </div>
                                                            <div>
                                                                <div className="cat-card__stat-label">Est. Base Weight</div>
                                                                <div className="cat-card__stat-value">{category.estBaseWeight} lbs</div>
                                                            </div>
                                                        </div>
                                                        <Link
                                                            to={`/category-management/edit/${category._id}`}
                                                            className="cat-card__configure"
                                                        >
                                                            Configure
                                                        </Link>
                                                    </div>
                                                </div>
                                            </article>
                                        );
                                    })
                                ) : (
                                    <div className="pcard-empty">
                                        <div className="pcard-empty__icon">
                                            <i className="fas fa-layer-group" />
                                        </div>
                                        <h3 className="pcard-empty__title">No categories yet</h3>
                                        <p className="pcard-empty__text">Add your first logistics category to get started.</p>
                                    </div>
                                )}
                            </div>

                            <div className="pcard-pagination">
                                <Pagination
                                    currentDocs={categories.length}
                                    totalDocs={data?.data?.data?.totalDocs}
                                    totalPages={Math.ceil(data?.data?.data?.totalPages || 0)}
                                    page={page}
                                    setPage={setPage}
                                />
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            <ModalImageViewer
                show={showImageViewer}
                images={viewerImages}
                startIndex={0}
                onHide={() => setShowImageViewer(false)}
                title="Category Image"
            />
        </div>
    );
}
